import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useUserContext } from '../../hooks/useUserContext';
import { userService } from '../../services/userService';
import { track } from '../../services/analytics';
import CpaxLogo from '../../components/CpaxLogo';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InviteRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  error: string | null;
}

type PageStatus = 'form' | 'submitting' | 'success';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyRow(): InviteRow {
  return { id: crypto.randomUUID(), email: '', firstName: '', lastName: '', error: null };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Sub-components ───────────────────────────────────────────────────────────

function AnimatedCheckBadge() {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 80,
        height: 80,
        mx: 'auto',
        mb: 3,
        '@keyframes badgePop': {
          '0%':   { transform: 'scale(0)', opacity: 0 },
          '70%':  { transform: 'scale(1.04)', opacity: 1 },
          '100%': { transform: 'scale(1)', opacity: 1 },
        },
        '@keyframes ringExpand': {
          '0%':   { transform: 'scale(1)', opacity: 0.5 },
          '100%': { transform: 'scale(1.75)', opacity: 0 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& > *': { animation: 'none !important', opacity: '1 !important' },
        },
      }}
    >
      {/* Expanding ring */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: 'primary.main',
          animation: 'ringExpand 0.9s ease-out 0.35s forwards',
          opacity: 0,
        }}
      />
      {/* Badge */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'badgePop 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards',
          opacity: 0,
        }}
      >
        <CheckIcon sx={{ fontSize: 40, color: 'white', strokeWidth: 1 }} />
      </Box>
    </Box>
  );
}

const STEPS = ['Sign Up', 'Choose Plan', 'Invite Your Team'];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BusinessInvite() {
  const { backendProfile, isLoaded, loading, refreshUser } = useUserContext();
  const navigate = useNavigate();

  const [rows, setRows] = useState<InviteRow[]>([emptyRow()]);
  const [status, setStatus] = useState<PageStatus>('form');
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [successCount, setSuccessCount] = useState(0);
  const [tenantPollCount, setTenantPollCount] = useState(0);

  const tenantId = backendProfile?.tenantId ?? null;

  // Auto-poll refreshUser up to 3x if tenantId is not yet populated
  // (Chargebee redirects before the webhook may have propagated)
  useEffect(() => {
    if (isLoaded && !loading && !tenantId && tenantPollCount < 3) {
      const timer = setTimeout(async () => {
        await refreshUser();
        setTenantPollCount(c => c + 1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isLoaded, loading, tenantId, tenantPollCount, refreshUser]);

  // ── Row handlers ──────────────────────────────────────────────────────────

  const handleRowChange = (id: string, field: 'email' | 'firstName' | 'lastName', value: string) => {
    setRows(prev =>
      prev.map(r =>
        r.id === id ? { ...r, [field]: value, error: null } : r
      )
    );
  };

  const handleAddRow = () => {
    if (rows.length >= 10) return;
    setRows(prev => [...prev, emptyRow()]);
  };

  const handleRemoveRow = (id: string) => {
    setRows(prev =>
      prev.length === 1
        ? [emptyRow()]
        : prev.filter(r => r.id !== id)
    );
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    let valid = true;
    setRows(prev => {
      // Build a set of emails seen so far to detect duplicates
      const seen = new Set<string>();
      return prev.map(r => {
        const trimmed = r.email.trim().toLowerCase();
        if (trimmed === '') return r; // empty rows skipped on submit
        if (!EMAIL_RE.test(trimmed)) {
          valid = false;
          return { ...r, error: 'Enter a valid email address' };
        }
        if (seen.has(trimmed)) {
          valid = false;
          return { ...r, error: 'Duplicate email address' };
        }
        if (!r.firstName.trim() || !r.lastName.trim()) {
          valid = false;
          return { ...r, error: 'First and last name are required' };
        }
        seen.add(trimmed);
        return r;
      });
    });
    return valid;
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!tenantId) return;
    const filledRows = rows.filter(r => r.email.trim() !== '');
    if (filledRows.length === 0) {
      navigate('/app/tenant-admin');
      return;
    }
    if (!validate()) return;

    setStatus('submitting');
    setGlobalError(null);

    const results = await Promise.allSettled(
      filledRows.map(r =>
        userService.inviteUser(tenantId, {
          email: r.email.trim(),
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          role: 'user',
        })
      )
    );

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    if (succeeded > 0) {
      track('user_invited', { role: 'user', count: succeeded });
      setSuccessCount(succeeded);
      setStatus('success');
      if (failed > 0) {
        setGlobalError(
          `${succeeded} invite${succeeded !== 1 ? 's' : ''} sent. ` +
          `${failed} failed. Check those addresses and retry from the Users page.`
        );
      }
    } else {
      setStatus('form');
      setGlobalError('No invites could be sent. Please check your network and try again.');
    }
  };

  const handleSkip = () => navigate('/app/tenant-admin');

  // ── Loading / waiting states ──────────────────────────────────────────────

  if (!isLoaded || loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // TenantId still null after 3 polls — show manual retry
  if (!tenantId && tenantPollCount >= 3) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <Paper elevation={2} sx={{ p: 4, maxWidth: 440, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Just a moment...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your organization is still being set up. This usually takes just a few seconds after checkout.
          </Typography>
          <Button
            variant="contained"
            onClick={async () => {
              setTenantPollCount(0);
              await refreshUser();
            }}
          >
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  // Still polling
  if (!tenantId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Setting up your organization...
        </Typography>
      </Box>
    );
  }

  // ── Page ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle', display: 'flex', flexDirection: 'column' }}>

      {/* Logo AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', zIndex: 1 }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <CpaxLogo height={28} showMark={false} />
        </Toolbar>
      </AppBar>

      {/* Stepper AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 852 }}>
            <Stepper
              activeStep={2}
              sx={{ '& .MuiStepLabel-label': { display: { xs: 'none', sm: 'block' } } }}
            >
              {STEPS.map((label, idx) => (
                <Step key={label} completed={idx < 2}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 8,
          pb: 6,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 608 }}>

          {status === 'success' ? (
            /* ── Success state ── */
            <Paper
              elevation={2}
              sx={{ p: { xs: 4, sm: 5 }, textAlign: 'center' }}
            >
              <AnimatedCheckBadge />
              <Typography
                sx={{
                  fontSize: 32,
                  fontWeight: 400,
                  lineHeight: 1.235,
                  letterSpacing: '0.25px',
                  color: 'primary.main',
                  mb: 1,
                }}
              >
                {successCount} invitation{successCount !== 1 ? 's' : ''} sent
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: globalError ? 2 : 4 }}>
                Your teammates will receive an email to join your organization.
              </Typography>
              {globalError && (
                <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                  {globalError}
                </Alert>
              )}
              <Button
                variant="contained"
                size="large"
                fullWidth
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/app/tenant-admin')}
                sx={{
                  px: '22px',
                  py: 1,
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  letterSpacing: '0.46px',
                }}
              >
                Go to Dashboard
              </Button>
            </Paper>
          ) : (
            /* ── Form state ── */
            <>
              {/* Hero */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AnimatedCheckBadge />
                <Typography
                  sx={{
                    fontSize: 32,
                    fontWeight: 400,
                    lineHeight: 1.235,
                    letterSpacing: '0.25px',
                    color: 'primary.main',
                    mb: 1,
                  }}
                >
                  Your organization is ready
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.43,
                    letterSpacing: '0.17px',
                    color: 'text.primary',
                  }}
                >
                  Invite your team to get started.
                </Typography>
              </Box>

              {/* Invite card */}
              <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Add team members
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  They'll receive an email invitation to join your organization.
                </Typography>

                {globalError && (
                  <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setGlobalError(null)}>
                    {globalError}
                  </Alert>
                )}

                {/* Invite rows */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  {rows.map((row, idx) => (
                    <Box
                      key={row.id}
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <TextField
                          label="First name"
                          required
                          size="small"
                          placeholder="Jane"
                          value={row.firstName}
                          onChange={e => handleRowChange(row.id, 'firstName', e.target.value)}
                          error={!!row.error && !row.firstName.trim()}
                          disabled={status === 'submitting'}
                          autoFocus={idx === 0}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Last name"
                          required
                          size="small"
                          placeholder="Smith"
                          value={row.lastName}
                          onChange={e => handleRowChange(row.id, 'lastName', e.target.value)}
                          error={!!row.error && !row.lastName.trim()}
                          disabled={status === 'submitting'}
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRow(row.id)}
                          disabled={status === 'submitting'}
                          sx={{
                            mt: 0.5,
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' },
                          }}
                          aria-label="Remove row"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', pr: 4.5 }}>
                        <TextField
                          label="Email address"
                          required
                          fullWidth
                          size="small"
                          placeholder="colleague@company.com"
                          value={row.email}
                          onChange={e => handleRowChange(row.id, 'email', e.target.value)}
                          error={!!row.error}
                          helperText={row.error ?? ''}
                          disabled={status === 'submitting'}
                          autoComplete="email"
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Add row */}
                {rows.length < 10 && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<PersonAddAlt1Icon />}
                    onClick={handleAddRow}
                    disabled={status === 'submitting'}
                    sx={{ mb: 3, fontWeight: 500 }}
                  >
                    Add another teammate
                  </Button>
                )}

                {/* Send Invites */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={status === 'submitting'}
                  sx={{
                    px: '22px',
                    py: 1,
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    letterSpacing: '0.46px',
                    mb: 1.5,
                  }}
                >
                  {status === 'submitting'
                    ? <CircularProgress size={22} color="inherit" />
                    : 'Send Invites'}
                </Button>

                {/* Skip */}
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleSkip}
                    disabled={status === 'submitting'}
                    sx={{ color: 'text.secondary', fontSize: 13 }}
                  >
                    Skip for now, go to dashboard
                  </Button>
                </Box>
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
